<script setup>
import {computed, ref} from 'vue'
import {Disclosure, DisclosureButton, DisclosurePanel} from '@headlessui/vue'
import {useI18n} from 'vue-i18n'
import {ChevronDownIcon, StarIcon} from '@heroicons/vue/24/outline'
import ArticlesItemImage from '@/components/ArticlesItemImage.vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import {formatNumber, getIfMatchForETags, statusStyles, ucfirst} from '@/utils'
import {getCountryName} from '@/utils/countries'

const props = defineProps({
  item: {type: Object, required: true},
  itemDetails: {type: Object, default: null},
  defaultOpen: {type: Boolean, default: false},
  fetchArticle: {type: Function, default: null},

  showFavorite: {type: Boolean, default: false},
  showLink: {type: Boolean, default: false},
  showDisclosure: {type: Boolean, default: false},
})

const {t: $t} = useI18n()

const itemDetails = ref(props.itemDetails)
const itemDetailsQuery = ref(null)
const isFetchingDetails = computed(() => itemDetailsQuery.value && !itemDetails.value)

function trackOpenState(open) {
  if (!open || itemDetails.value || itemDetailsQuery.value || !props.fetchArticle) return
  itemDetailsQuery.value = props.fetchArticle(props.item.articleNumber)
  itemDetailsQuery.value.then((data) => {
    itemDetails.value = data
  })
}

async function toggleFavorite() {
  const setFavorite = !props.item.isFavorite
  try {
    await fetch(`/rest/shop/v1/articles/${props.item.articleNumber}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'If-Match': getIfMatchForETags(props.item.etag),
      },
      body: JSON.stringify({isFavorite: setFavorite}),
    })
    props.item.isFavorite = setFavorite
  } catch (e) {
    console.error($t('Could not favorite article. Please try again later.'), e)
  }
}

function getFlagUrl(code) {
  return new URL(settings.siteAddress + `src/img/flags/${code.toLowerCase()}.svg`, import.meta.url)
    .href
}

const inventoryColumns = [
  {key: 'dimension', label: 'Dimensions', width: 'w-[13%]'},
  {key: 'grade', label: 'Grade', width: 'w-[24%]'},
  {key: 'tolerance', label: 'Standard', width: 'w-[14%]'},
  {key: 'type', label: 'Type', width: 'w-[6%]'},
  {key: 'meters', label: 'Meters', width: 'w-[10%]'},
  {key: 'pieces', label: 'Pcs', width: 'w-[6%]'},
  {key: 'minMax', label: 'Min / Max length', width: 'w-[20%]'},
  {key: 'origin', label: 'Origin', width: 'w-[7%]', isFlag: true},
]

function getCellValue(col, pos) {
  switch (col.key) {
    case 'dimension': {
      let dimensions = pos.outerDiameter
      if (pos.wallThickness) {
        dimensions += ' x ' + pos.wallThickness
      }
      return dimensions + ' mm'
    }
    case 'grade':
      return pos.grade
    case 'tolerance':
      return pos.tolerance
    case 'type':
      return pos.type
    case 'meters':
      return `${formatNumber(pos.meters)} m`
    case 'pieces':
      return pos.pieces
    case 'minMax':
      return `${formatNumber(pos.minimumLength)} – ${formatNumber(pos.maximumLength)} m`
    default:
      return ''
  }
}
</script>

<template>
  <Disclosure v-slot="{open}" :defaultOpen="defaultOpen">
    <div
      class="transition-all mt-4 relative rounded-lg bg-white shadow hover:shadow-lg"
      :class="{
        'divide-y divide-gray-200': showDisclosure,
      }"
    >
      <component
        :is="showDisclosure ? DisclosureButton : 'div'"
        :disabled="showDisclosure ? isFetchingDetails : undefined"
        class="default-grid !grid-cols-24 gap-2 w-full text-left items-start !p-3"
        :class="{'cursor-default': !showDisclosure}"
      >
        <div
          v-if="showFavorite"
          class="absolute top-1.5 right-[14px] text-gray-400"
          :class="{'text-yellow-400': item.isFavorite}"
        >
          <StarIcon class="size-2.5" @click.prevent="toggleFavorite" />
        </div>

        <div v-if="showDisclosure" class="absolute top-10 right-2">
          <span class="sr-only">{{ $t('Show details') }}</span>
          <ChevronDownIcon class="size-2" :class="{'rotate-180': open}" />
        </div>

        <div class="flex gap-3 col-span-24 lg:col-span-8">
          <div class="hidden lg:block">
            <div class="w-[26px] aspect-square">
              <ArticlesItemImage
                :outer-diameter="item.outerDiameter"
                :wall-thickness="item.wallThickness"
                :shape="item.shape"
              />
            </div>
          </div>
          <div>
            <div class="font-semibold">
              <component
                :is="showLink ? 'a' : 'span'"
                :href="showLink ? `/articles/${item.articleNumber}` : undefined"
                class="font-semibold"
                @click.stop
              >
                {{ ucfirst(item.articleGroupName) }}
              </component>
            </div>
            <div class="text-sm text-gray-500">
              {{ $t('Art.nr.') }}: {{ item.customerArticleNumber || item.articleNumber }}
              <div class="mt-0.5 text-xs text-gray-500 pb-[3px]">{{ item.unitMass.mass }} kg/m</div>
            </div>
          </div>
        </div>

        <div class="col-span-24 lg:col-span-5 pt-0.5">
          <div class="key-label">{{ $t('Dimensions') }}</div>
          <div
            class="mt-1 inline-block px-[9px] py-[5px] font-medium bg-mertl-blue text-white rounded-md text-sm whitespace-nowrap"
          >
            {{ item.outerDiameter }}
            <span v-if="item.wallThickness">x {{ item.wallThickness }}</span> mm
          </div>
        </div>

        <div
          :class="item.type ? 'col-span-24 lg:col-span-8' : 'col-span-24 lg:col-span-11'"
          class="pt-0.5"
        >
          <div class="key-label">{{ $t('Grade') }}</div>
          <div class="mt-1 flex flex-wrap gap-0.5">
            <div v-for="grade in item.grades" :key="grade">
              <div
                class="inline-block px-[9px] py-[5px] font-medium bg-mertl-grey text-white rounded-md text-sm"
              >
                {{ grade }}
              </div>
            </div>
          </div>
        </div>

        <div v-if="item.type" class="col-span-24 lg:col-span-3 pt-0.5">
          <div class="key-label">{{ $t('Type') }}</div>
          <div class="mt-1 flex flex-wrap gap-0.5">
            <div class="inline-block py-[5px] font-medium text-sm">
              {{ item.type }}
            </div>
          </div>
        </div>

        <div class="col-span-24 lg:pl-[50px] text-xs text-gray-500">{{ item.articleText }}</div>

        <div v-if="item.inventory?.length" class="col-span-24 lg:pl-[50px]">
          <div class="text-sm mt-2">
            <span class="font-semibold">{{ $t('Short lengths') }}</span> ({{
              item.inventory.length
            }}
            {{ item.inventory.length === 1 ? $t('position') : $t('positions') }})
          </div>

          <!-- Mobile: stacked cards -->
          <div class="xl:hidden mt-2 text-sm divide-y divide-gray-200 border-t border-gray-200">
            <div
              v-for="(pos, i) in item.inventory"
              :key="i"
              class="py-2 grid grid-cols-1 gap-x-4 gap-y-0.5"
            >
              <template v-for="col in inventoryColumns" :key="col.key">
                <div
                  v-if="col.isFlag ? pos.origin : getCellValue(col, pos)"
                  class="flex gap-2 justify-between"
                >
                  <span class="key-label">{{ $t(col.label) }}:</span>
                  <img
                    v-if="col.isFlag"
                    :src="getFlagUrl(pos.origin)"
                    :alt="pos.origin"
                    :title="getCountryName(pos.origin)"
                    class="inline w-2.5 h-auto ml-1"
                  />
                  <template v-else>{{ getCellValue(col, pos) }}</template>
                </div>
              </template>
            </div>
          </div>

          <!-- Desktop: table -->
          <table class="hidden xl:table w-full table-fixed border-collapse mt-2">
            <thead>
              <tr>
                <th
                  v-for="col in inventoryColumns"
                  :key="col.key"
                  class="key-label pb-1"
                  :class="col.width"
                >
                  {{ $t(col.label) }}
                </th>
              </tr>
            </thead>
            <tbody class="text-sm divide-y divide-gray-200 pt-1 border-t border-gray-200">
              <tr v-for="(pos, i) in item.inventory" :key="i">
                <td v-for="col in inventoryColumns" :key="col.key" class="py-0.5">
                  <img
                    v-if="col.isFlag"
                    :src="getFlagUrl(pos.origin)"
                    :alt="pos.origin"
                    :title="getCountryName(pos.origin)"
                    class="w-2.5 h-auto border border-gray-200"
                  />
                  <template v-else>{{ getCellValue(col, pos) }}</template>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <template v-if="showDisclosure">
          <div
            class="col-span-3 pt-0.5"
            :class="[Object.hasOwn(item, 'quantity') ? '' : 'invisible']"
          >
            <div class="key-label">{{ $t('In stock') }}</div>
            <div class="key-value">{{ formatNumber(item.quantity) }} m</div>
          </div>
          <div class="col-span-3 pt-0.5" :class="[item.quantityOrdered ? '' : 'invisible']">
            <div class="key-label">{{ $t('Ordered') }}</div>
            <div class="key-value">{{ formatNumber(item.quantityOrdered) }} m</div>
          </div>
        </template>

        <LoadingSpinner v-if="isFetchingDetails" />
      </component>

      <transition
        v-if="showDisclosure"
        enter-active-class="transition duration-100 ease-out"
        enter-from-class="transform scale-100 opacity-0"
        enter-to-class="transform scale-100 opacity-100"
        leave-active-class="transition duration-75 ease-out"
        leave-from-class="transform scale-100 opacity-100"
        leave-to-class="transform scale-100 opacity-0"
      >
        <DisclosurePanel>
          <LoadingSpinner v-if="!itemDetails && isFetchingDetails" />
          <!-- <ArticlesItemDetails v-else-if="itemDetails" :item="item" :itemDetails="itemDetails" /> -->
        </DisclosurePanel>
      </transition>

      <template v-if="showDisclosure && trackOpenState(open)"></template>
    </div>
  </Disclosure>
</template>
